import { toLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';

import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';

import { CommentManager } from 'models';

import MouseClick from 'controllers/MouseClick';
import {
    AbstractLayer,
    AbstractLayerProps,
    LayerProps,
} from 'controllers/AbstractLayer';
import { Identifiable, ShowOverlay } from 'controllers';
import { Fill, Stroke, Text } from 'ol/style';

const ADD_COMMENT_OVERLAY = 'add-comment';

export type CommentsLayerClick = (
    comment: CommentManager.Comment[],
    coords: number[],
) => void;

/**
 * The CommentsLayer manages the map comments along with
 * the action surrounding adding a new comment.
 *
 * It's a VectorLayer with a custom datasource that
 * gets its comments via the `updateComments` method.
 *
 * This layer is a unique example of how to implement
 * the ActionButton functionality.
 */
export class CommentsLayer extends AbstractLayer<
    VectorLayer<VectorSource<Point>>
> {
    private source: VectorSource<Point> | null;

    private onCommentClick?: CommentsLayerClick;

    exitEvent: (event: KeyboardEvent) => void;

    onCancelClick?: () => void;

    mouseClick?: MouseClick;

    constructor({ config, opacity, visible, zIndex }: AbstractLayerProps) {
        super({
            config,
            opacity,
            visible,
            zIndex,
            action: {
                title: 'Add Comment',
            },
        });
        this.source = new VectorSource({
            features: [],
        });

        this.exitEvent = (event) => {
            if (event.key === 'Escape') {
                this.clearClick();
            }
        };
    }

    public clearClick() {
        this.mouseClick?.clear();
        this.mouseClick = undefined;
        document.removeEventListener('keyup', this.exitEvent);
        if (this.onCancelClick) this.onCancelClick();
    }

    public onActionClick(onCancel?: () => void) {
        if (this.dispatch) {
            this.onCancelClick = onCancel;
            const click = new MouseClick(
                (position) => {
                    if (this.dispatch)
                        this.dispatch(
                            new ShowOverlay(
                                ADD_COMMENT_OVERLAY,
                                position,
                                true,
                            ),
                        );
                    this.clearClick();
                },
                true,
                true,
            );

            this.mouseClick = click;

            document.addEventListener('keyup', this.exitEvent);

            this.dispatch(click);
        }
    }

    public getCommentsAtLocation = (
        coords: number[],
    ): CommentManager.Comment[] => {
        if (!this.layer) return [];
        if (!this.map) return [];

        const features = this.map.getFeaturesAtPixel(
            this.map.getPixelFromCoordinate(coords),
            {
                hitTolerance: 5,
            },
        );

        if (!features || features.length === 0) return [];

        const comments: CommentManager.Comment[] = [];

        features.forEach((feature) => {
            const comment = feature.get(
                'comment',
            ) as CommentManager.Comment | null;
            if (comment) comments.push(comment);
        });

        return comments;
    };

    identifiable: Identifiable['identifiable'] = (event) => {
        const comments = this.getCommentsAtLocation(event.coordinate);

        // The comments layer isn't identifiable
        // If none were found so return false to allow identify to continue
        if (comments.length === 0 || !this.onCommentClick) return false;

        // We found comments, we want the identify to stop
        this.onCommentClick(comments, toLonLat(event.coordinate));
        return 'stop';
    };

    applyAuthentication() {}

    isAwaitingClick() {
        return !!this.mouseClick;
    }

    setCommentClick(onClick: CommentsLayerClick) {
        this.onCommentClick = onClick;
    }

    updateComments(comments: CommentManager.Comment[]) {
        if (this.source) {
            this.source.clear();
            comments.forEach((comment) => {
                const feat = new Feature({
                    geometry: comment.geometry,
                    comment,
                });

                this.source?.addFeature(feat);
            });
        }
    }

    initializeLayer(props: LayerProps) {
        const style = new Style({
            image: new Icon({
                src: '/thumbnails/comment.svg',
                opacity: 1,
                scale: 1,
                // anchorOrigin: 'bottom-left',
            }),
            text: new Text({
                font: '12px Calibri,sans-serif',
                fill: new Fill({ color: '#FFF000' }),
                stroke: new Stroke({
                    color: '#005900',
                    width: 5,
                }),
                offsetX: 20,
                offsetY: -1,
            }),
        });
        const layer = new VectorLayer({
            ...props,
            source: this.source || undefined,
            style: function (feature) {
                style
                    .getText()
                    .setText(`${feature.get('comment').bmcCommentId}`);
                return style;
            },
        });

        return layer;
    }
}

export default CommentsLayer;
